import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import BackButton from '../components/ui/BackButton';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  EDUCATION_OPTIONS,
  ISLAMIC_EDUCATION_OPTIONS,
  JOB_CATEGORIES,
  OCCUPATION_OPTIONS,
  getAllJobTitles,
  getCategoryFromJob,
  MADHHAB_OPTIONS,
  PRAYER_HABIT_OPTIONS,
  BEARD_OPTIONS,
  HIJAB_NIQAB_OPTIONS,
  QURAN_RECITATION_OPTIONS,
  RELIGIOUS_COMMITMENT_OPTIONS,
  ISLAMIC_PRACTICES_OPTIONS,
  FAMILY_RELIGIOUS_LEVEL_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  INCOME_RANGES,
  INCOME_PERIOD_OPTIONS,
  CURRENCY_OPTIONS,
  HEIGHT_OPTIONS,
  LANGUAGE_OPTIONS,
  CANADIAN_PROVINCES,
  BUILD_OPTIONS
} from '../constants/profileOptions';
import { COUNTRIES, getProvincesForCountry } from '../constants/countries';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EditProfilePage = () => {
  const [searchParams] = useSearchParams();
  const isMandatoryMode = searchParams.get('mandatory') === 'true';
  
  // Required fields for mandatory profile completion (DOB already collected in registration)
  const MANDATORY_FIELDS = ['firstName', 'lastName', 'image', 'country', 'province', 'about', 'motherTongue'];
  
  const [formData, setFormData] = useState({
    familyDetails: {
      familyStatus: '',
      familyType: '',
      fatherOccupation: '',
      motherOccupation: '',
      siblings: '',
      familyReligiousLevel: '',
      numberOfSiblings: 0,
      livingWithFamily: false
    },
    partnerPreferences: {
      ageRange: '',
      heightRange: '',
      maritalStatus: '',
      religion: '',
      education: '',
      occupation: '',
      countryPreference: '',
      cityPreference: '',
      professionPreference: '',
      religiousLevelPreference: '',
      hijabPreference: '',
      beardPreference: '',
      otherPreferences: ''
    },
    religiousInfo: {
      madhhab: '',
      prayerHabit: '',
      beard: '',
      hijabNiqab: '',
      quranRecitation: '',
      religiousCommitment: '',
      islamicPractices: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [missingFields, setMissingFields] = useState([]);
  const { user, isAuthenticated, refreshUser } = useAuth();
  
  // For cascading country/province dropdowns
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check which mandatory fields are missing
  const checkMandatoryFields = (data) => {
    const missing = [];
    if (!data.firstName) missing.push('First Name');
    if (!data.lastName) missing.push('Last Name');
    if (!data.image && !imagePreview) missing.push('Profile Picture');
    if (!data.country) missing.push('Country');
    if (!data.province) missing.push('Province/State');
    if (!data.about) missing.push('About Me');
    if (!data.motherTongue) missing.push('Language');
    return missing;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnUrl: '/edit-profile' } });
      return;
    }
    loadUserProfile();
  }, [isAuthenticated, navigate]);

  // Update missing fields whenever form data changes
  useEffect(() => {
    if (isMandatoryMode) {
      setMissingFields(checkMandatoryFields(formData));
    }
  }, [formData, imagePreview, isMandatoryMode]);

  const loadUserProfile = async () => {
    try {
      const response = await userAPI.getCurrentUser();
      const userData = response.user || response;
      setFormData({
        ...userData,
        familyDetails: userData.familyDetails || formData.familyDetails,
        partnerPreferences: userData.partnerPreferences || formData.partnerPreferences,
        religiousInfo: userData.religiousInfo || formData.religiousInfo
      });
      setImagePreview(userData.image || '');
      
      // Set available provinces based on user's country
      if (userData.country) {
        const provinces = getProvincesForCountry(userData.country);
        setAvailableProvinces(provinces);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "Image size should be less than 5MB",
          variant: "destructive",
        });
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveCurrentSection = async (isFinalSave = false) => {
    setSaving(true);
    try {
      console.log('Saving profile data:', formData);
      
      // If there's a new profile image, upload it first to Cloudinary
      if (profileImage) {
        try {
          const formDataImage = new FormData();
          formDataImage.append('file', profileImage);
          
          const uploadResponse = await axios.post(
            `${API_URL}/api/user/upload-profile-picture`,
            formDataImage,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${localStorage.getItem('token')}`
              },
              withCredentials: true
            }
          );
          
          if (uploadResponse.data.success) {
            formData.image = uploadResponse.data.url;
            setProfileImage(null); // Clear the image after successful upload
          }
        } catch (uploadError) {
          console.error('Error uploading profile picture:', uploadError);
          toast({
            title: "Upload Error",
            description: uploadError.response?.data?.detail || "Failed to upload profile picture",
            variant: "destructive",
          });
          setSaving(false);
          return false;
        }
      }
      
      // Add flag to mark final save
      const dataToSave = { ...formData, isFinalSave };
      
      const response = await userAPI.updateProfile(dataToSave);
      console.log('Update response:', response);
      
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
      
      if (response?.warning) {
        toast({
          title: "Some fields could not be saved",
          description: response.warning,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success!",
          description: isFinalSave ? "Profile completed successfully!" : "Section saved successfully",
        });
      }
      
      // In mandatory mode, verify completion against FRESH server data before redirecting
      if (isMandatoryMode && isFinalSave) {
        const freshResponse = await userAPI.getCurrentUser();
        const freshUser = freshResponse.user || freshResponse;
        const stillMissing = checkMandatoryFields(freshUser);
        if (stillMissing.length === 0) {
          navigate('/search');
        } else {
          setMissingFields(stillMissing);
          toast({
            title: "Profile not complete yet",
            description: `Still missing: ${stillMissing.join(', ')}. Please fill and save again.`,
            variant: "destructive",
          });
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update profile",
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    
    // Save current section
    const saved = await saveCurrentSection(false);
    if (!saved) return;
    
    // Move to next tab
    const tabOrder = ['basic', 'family', 'partner', 'religious'];
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // This is the final save from the last section
    const saved = await saveCurrentSection(true);
    if (saved && !isMandatoryMode) {
      if (refreshUser) await refreshUser();
      navigate('/profile');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle country change - update provinces
    if (name === 'country') {
      const provinces = getProvincesForCountry(value);
      setAvailableProvinces(provinces);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        province: '' // Reset province when country changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  // Check if profile is locked (govIdVerified and isNameLocked is not explicitly false)
  // If govIdVerified is true but isNameLocked is undefined/null, treat as locked
  const isProfileLocked = formData.govIdVerified && formData.isNameLocked !== false;

  // Check if all mandatory fields are complete
  const isMandatoryComplete = missingFields.length === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-8">
        {!isMandatoryMode && <BackButton />}
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isMandatoryMode ? 'Complete Your Profile' : 'Edit Profile'}
        </h1>
        <p className="text-gray-600 mb-6">
          {isMandatoryMode 
            ? 'Please complete the required fields to continue using the platform'
            : 'Update your matrimonial profile information'
          }
        </p>

        {/* Mandatory Fields Banner */}
        {isMandatoryMode && missingFields.length > 0 && (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-800">Required Fields Missing</h3>
                <p className="text-sm text-red-700 mb-2">
                  Please fill in the following required fields to continue:
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {missingFields.map(field => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Success Banner when all mandatory fields complete */}
        {isMandatoryMode && missingFields.length === 0 && (
          <div className="mb-6 bg-green-50 border-2 border-green-300 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="text-green-500 text-2xl">✓</div>
              <div>
                <h3 className="font-bold text-green-800">All Required Fields Complete!</h3>
                <p className="text-sm text-green-700">
                  Click "Save Profile" below to save your changes and continue.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Locked Banner */}
        {isProfileLocked && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <div className="text-amber-500 text-xl">🔒</div>
            <div>
              <h3 className="font-semibold text-amber-800">Some fields are locked</h3>
              <p className="text-sm text-amber-700">
                Your profile picture, name, date of birth, gender, and location are locked after ID verification. 
                This ensures your verified information stays accurate. To make changes, please contact support.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
              activeTab === 'basic' ? 'bg-mint-500 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Basic Info
          </button>
          <button
            onClick={() => setActiveTab('family')}
            className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
              activeTab === 'family' ? 'bg-mint-500 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Family Details
          </button>
          <button
            onClick={() => setActiveTab('partner')}
            className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
              activeTab === 'partner' ? 'bg-mint-500 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Partner Preferences
          </button>
          <button
            onClick={() => setActiveTab('religious')}
            className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
              activeTab === 'religious' ? 'bg-mint-500 text-white' : 'bg-white text-gray-700'
            }`}
          >
            Religious Information
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardContent className="p-6">
              {/* Rest of the large form content continues... Due to character limits, I'll need to use multiple messages */}
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h2>
                  
                  {/* Profile Picture Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profile Picture
                      {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                      {isProfileLocked && (
                        <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                      )}
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={imagePreview || 'https://via.placeholder.com/150'}
                          alt="Profile"
                          className={`w-32 h-32 rounded-full object-cover border-4 ${isProfileLocked ? 'border-amber-200' : 'border-gray-200'}`}
                        />
                        {isProfileLocked && (
                          <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">🔒</div>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="profile-picture"
                          disabled={isProfileLocked}
                        />
                        <label
                          htmlFor="profile-picture"
                          className={`cursor-pointer px-6 py-2 rounded-lg transition-colors inline-block ${
                            isProfileLocked
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-mint-500 text-white hover:bg-mint-600'
                          }`}
                        >
                          {isProfileLocked ? '🔒 Locked' : 'Change Picture'}
                        </label>
                        <p className="text-sm text-gray-500 mt-2">
                          {isProfileLocked 
                            ? 'Contact support to unlock this field' 
                            : 'JPG, PNG or JPEG. Max size 5MB'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                        {isProfileLocked && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                        )}
                      </label>
                      <input 
                        type="text" 
                        name="firstName" 
                        value={formData.firstName || ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isProfileLocked
                            ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                            : isMandatoryMode && !formData.firstName
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        placeholder="Enter first name"
                        required
                        disabled={isProfileLocked}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                        {isProfileLocked && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                        )}
                      </label>
                      <input 
                        type="text" 
                        name="lastName" 
                        value={formData.lastName || ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isProfileLocked
                            ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                            : isMandatoryMode && !formData.lastName
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        placeholder="Enter last name"
                        disabled={isProfileLocked}
                      />
                    </div>
                  </div>
                  
                  {/* Date of Birth Field (already collected in registration, shown for reference) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth
                        {isProfileLocked && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                        )}
                      </label>
                      <input 
                        type="date" 
                        name="dob" 
                        value={formData.dob ? formData.dob.split('T')[0] : ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isProfileLocked
                            ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        disabled={isProfileLocked}
                      />
                      {formData.age && (
                        <p className="text-xs text-gray-500 mt-1">Age: {formData.age} years</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        About Me
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea 
                        name="about" 
                        value={formData.about || ''} 
                        onChange={handleChange} 
                        rows="4" 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isMandatoryMode && !formData.about
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        placeholder="Tell us about yourself..." 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                      <select name="height" value={formData.height || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Height</option>{HEIGHT_OPTIONS.map(height => (<option key={height} value={height}>{height}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                      <input 
                        type="number" 
                        name="weight" 
                        value={formData.weight || ''} 
                        onChange={handleChange} 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" 
                        placeholder="e.g., 70"
                        min="30"
                        max="300"
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional - visible to other users</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Build</label>
                      <select 
                        name="build" 
                        value={formData.build || ''} 
                        onChange={handleChange} 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"
                      >
                        <option value="">Select Build</option>
                        {BUILD_OPTIONS.map(build => (
                          <option key={build} value={build}>{build}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Optional - visible to other users</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                        {isProfileLocked && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                        )}
                      </label>
                      <select 
                        name="country" 
                        value={formData.country || ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isProfileLocked
                            ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                            : isMandatoryMode && !formData.country
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        disabled={isProfileLocked}
                      >
                        <option value="">Select Country</option>
                        {COUNTRIES.map(country => (
                          <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Province/State
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                        {isProfileLocked && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">🔒 Locked</span>
                        )}
                      </label>
                      <select 
                        name="province" 
                        value={formData.province || ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isProfileLocked
                            ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                            : isMandatoryMode && !formData.province
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                        disabled={isProfileLocked || !formData.country}
                      >
                        <option value="">Select Province/State</option>
                        {availableProvinces.map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </select>
                      {!formData.country && !isProfileLocked && (
                        <p className="text-xs text-gray-500 mt-1">Select country first</p>
                      )}
                    </div>
                    <div style={{display: 'none'}}>
                      <input 
                        type="text" 
                        name="address" 
                        value={formData.address || ''} 
                        readOnly 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed" 
                        placeholder="Your Address" 
                      />
                      <p className="text-xs text-gray-500 mt-1">Address is locked after verification</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language
                        {isMandatoryMode && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <select 
                        name="motherTongue" 
                        value={formData.motherTongue || ''} 
                        onChange={handleChange} 
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                          isMandatoryMode && !formData.motherTongue
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-mint-500'
                        }`}
                      >
                        <option value="">Select Language</option>
                        {LANGUAGE_OPTIONS.map(language => (
                          <option key={language} value={language}>{language}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status</label>
                      <select name="maritalStatus" value={formData.maritalStatus || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Status</option>{MARITAL_STATUS_OPTIONS.map(status => (<option key={status} value={status}>{status}</option>))}</select>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-4">Education</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Regular Education</label>
                      <select name="education" value={formData.education || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Education</option>{EDUCATION_OPTIONS.map(edu => (<option key={edu} value={edu}>{edu}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Islamic Education</label>
                      <select name="islamicEducation" value={formData.islamicEducation || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Islamic Education</option>{ISLAMIC_EDUCATION_OPTIONS.map(edu => (<option key={edu} value={edu}>{edu}</option>))}</select>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-4">Profession</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Category</label>
                      <select name="jobCategory" value={formData.jobCategory || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Category</option>{Object.keys(JOB_CATEGORIES).map(category => (<option key={category} value={category}>{category}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Title / Occupation</label>
                      <select name="occupation" value={formData.occupation || ''} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500">
                        <option value="">Select Occupation</option>
                        {OCCUPATION_OPTIONS.map(occupation => (
                          <option key={occupation} value={occupation}>{occupation}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                      <select name="incomeCurrency" value={formData.incomeCurrency || 'USD'} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500">
                        {CURRENCY_OPTIONS.map(currency => (
                          <option key={currency.code} value={currency.code}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Income Range</label>
                      <div className="flex gap-2">
                        <select name="income" value={formData.income || ''} onChange={handleChange} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500">
                          <option value="">Select Income</option>
                          {INCOME_RANGES.map(range => {
                            const selectedCurrency = CURRENCY_OPTIONS.find(c => c.code === (formData.incomeCurrency || 'USD'));
                            const symbol = selectedCurrency?.symbol || '$';
                            return (
                              <option key={range} value={range}>
                                {range === 'Prefer Not to Say' ? range : `${symbol}${range}`}
                              </option>
                            );
                          })}
                        </select>
                        <select name="incomePeriod" value={formData.incomePeriod || 'yearly'} onChange={handleChange} className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500">
                          {INCOME_PERIOD_OPTIONS.map(period => (
                            <option key={period.value} value={period.value}>{period.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'religious' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Religious Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Madhhab (School of Thought)</label>
                      <select value={formData.religiousInfo?.madhhab || ''} onChange={(e) => handleNestedChange('religiousInfo', 'madhhab', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Madhhab</option>{MADHHAB_OPTIONS.map(madhhab => (<option key={madhhab} value={madhhab}>{madhhab}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prayer Habit</label>
                      <select value={formData.religiousInfo?.prayerHabit || ''} onChange={(e) => handleNestedChange('religiousInfo', 'prayerHabit', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Prayer Habit</option>{PRAYER_HABIT_OPTIONS.map(habit => (<option key={habit} value={habit}>{habit}</option>))}</select>
                    </div>
                    {formData.gender === 'male' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Beard</label>
                        <select value={formData.religiousInfo?.beard || ''} onChange={(e) => handleNestedChange('religiousInfo', 'beard', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Status</option>{BEARD_OPTIONS.map(option => (<option key={option} value={option}>{option}</option>))}</select>
                      </div>
                    )}
                    {formData.gender === 'female' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hijab / Niqab</label>
                        <select value={formData.religiousInfo?.hijabNiqab || ''} onChange={(e) => handleNestedChange('religiousInfo', 'hijabNiqab', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Status</option>{HIJAB_NIQAB_OPTIONS.map(option => (<option key={option} value={option}>{option}</option>))}</select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quran Recitation Level</label>
                      <select value={formData.religiousInfo?.quranRecitation || ''} onChange={(e) => handleNestedChange('religiousInfo', 'quranRecitation', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Level</option>{QURAN_RECITATION_OPTIONS.map(level => (<option key={level} value={level}>{level}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Religious Commitment</label>
                      <select value={formData.religiousInfo?.religiousCommitment || ''} onChange={(e) => handleNestedChange('religiousInfo', 'religiousCommitment', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Commitment Level</option>{RELIGIOUS_COMMITMENT_OPTIONS.map(level => (<option key={level} value={level}>{level}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Islamic Practices</label>
                      <select value={formData.religiousInfo?.islamicPractices || ''} onChange={(e) => handleNestedChange('religiousInfo', 'islamicPractices', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Practice Level</option>{ISLAMIC_PRACTICES_OPTIONS.map(practice => (<option key={practice} value={practice}>{practice}</option>))}</select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'family' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Family Background</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Father's Profession</label>
                      <input type="text" value={formData.familyDetails?.fatherOccupation || ''} onChange={(e) => handleNestedChange('familyDetails', 'fatherOccupation', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Father's occupation" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Profession</label>
                      <input type="text" value={formData.familyDetails?.motherOccupation || ''} onChange={(e) => handleNestedChange('familyDetails', 'motherOccupation', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Mother's occupation" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Family Religious Level</label>
                      <select value={formData.familyDetails?.familyReligiousLevel || ''} onChange={(e) => handleNestedChange('familyDetails', 'familyReligiousLevel', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Select Level</option>{FAMILY_RELIGIOUS_LEVEL_OPTIONS.map(level => (<option key={level} value={level}>{level}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Number of Siblings</label>
                      <input type="number" min="0" value={formData.familyDetails?.numberOfSiblings || 0} onChange={(e) => handleNestedChange('familyDetails', 'numberOfSiblings', parseInt(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Family Status</label>
                      <input type="text" value={formData.familyDetails?.familyStatus || ''} onChange={(e) => handleNestedChange('familyDetails', 'familyStatus', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="e.g., Middle Class, Upper Middle" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Family Type</label>
                      <input type="text" value={formData.familyDetails?.familyType || ''} onChange={(e) => handleNestedChange('familyDetails', 'familyType', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="e.g., Nuclear, Joint" />
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" id="livingWithFamily" checked={formData.familyDetails?.livingWithFamily || false} onChange={(e) => handleNestedChange('familyDetails', 'livingWithFamily', e.target.checked)} className="w-4 h-4 text-navy-500 border-gray-300 rounded focus:ring-mint-500" />
                      <label htmlFor="livingWithFamily" className="ml-2 text-sm font-medium text-gray-700">Currently living with family</label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'partner' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Partner Preferences</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
                      <input type="text" value={formData.partnerPreferences?.ageRange || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'ageRange', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="e.g., 25-30" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Height Range</label>
                      <input type="text" value={formData.partnerPreferences?.heightRange || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'heightRange', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="e.g., 5'6 - 6'0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status Preference</label>
                      <select value={formData.partnerPreferences?.maritalStatus || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'maritalStatus', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Any</option>{MARITAL_STATUS_OPTIONS.map(status => (<option key={status} value={status}>{status}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Education Preference</label>
                      <select value={formData.partnerPreferences?.education || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'education', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Any</option>{EDUCATION_OPTIONS.map(edu => (<option key={edu} value={edu}>{edu}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profession Preference</label>
                      <input type="text" value={formData.partnerPreferences?.professionPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'professionPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Preferred profession" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Religious Level Preference</label>
                      <select value={formData.partnerPreferences?.religiousLevelPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'religiousLevelPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">Any</option>{RELIGIOUS_COMMITMENT_OPTIONS.map(level => (<option key={level} value={level}>{level}</option>))}</select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country Preference</label>
                      <input type="text" value={formData.partnerPreferences?.countryPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'countryPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Preferred country" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City Preference</label>
                      <input type="text" value={formData.partnerPreferences?.cityPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'cityPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Preferred city" />
                    </div>
                    {formData.gender === 'male' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hijab Preference</label>
                        <select value={formData.partnerPreferences?.hijabPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'hijabPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">No Preference</option>{HIJAB_NIQAB_OPTIONS.map(option => (<option key={option} value={option}>{option}</option>))}</select>
                      </div>
                    )}
                    {formData.gender === 'female' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Beard Preference</label>
                        <select value={formData.partnerPreferences?.beardPreference || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'beardPreference', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500"><option value="">No Preference</option>{BEARD_OPTIONS.map(option => (<option key={option} value={option}>{option}</option>))}</select>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Other Preferences</label>
                      <textarea value={formData.partnerPreferences?.otherPreferences || ''} onChange={(e) => handleNestedChange('partnerPreferences', 'otherPreferences', e.target.value)} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500" placeholder="Any other specific preferences..." />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            {activeTab !== 'religious' ? (
              <Button 
                type="button" 
                onClick={handleNext} 
                disabled={saving} 
                className="flex-1 bg-mint-500 hover:bg-mint-600 text-white py-3 rounded-lg font-medium"
              >
                {saving ? 'Saving...' : 'Next'}
              </Button>
            ) : (
              <Button 
                type="submit" 
                disabled={saving} 
                className="flex-1 bg-mint-500 hover:bg-mint-600 text-white py-3 rounded-lg font-medium"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            )}
            <Button type="button" onClick={() => navigate('/profile')} className="px-8 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-medium">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfilePage;